/**
 * AdminCourseEdit.jsx
 * Create or edit a course in the admin dashboard.
 *
 * Updated features:
 *   - Side-by-side real-time Course Preview (with disable toggle)
 *   - Tool logo file upload directly to GridFS (saving URL to toolsCovered)
 *   - Copy section data from existing courses in the database
 *   - Custom IconDropdown component rendering actual React Icons
 *   - Removed unused fields: 3. Important Notice and testimonialLink in footer
 *
 * The form is organised into collapsible sections:
 *   1. Basic Info (slug, title, shortTitle, publish controls)
 *   2. Hero Section (headline, subheadline, description, statistics)
 *   3. Program Highlights (duration + feature rows with custom icon picker)
 *   4. Tools Covered (name, description, image upload / URL)
 *   5. Why Choose Us (reason cards)
 *   6. Program Details (overview phases, modeOfDelivery, commitment,
 *                       curriculum, mentorship, certification, careerAdvancement)
 *   7. Who Should Apply (with custom icon picker)
 *   8. Alumni Videos
 *   9. Footer / Misc (contactEmail, footerNote)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../homepage/navbar/Navbar';
import { useThemeContext } from '../../context/theme-context';
import { FiSave } from 'react-icons/fi';
import CourseDetailPreview from './CourseDetailPreview';
import { resolveIcon } from '../internship/utils/iconMap';
import './admin.css';

// Available icon keys (shown in the custom dropdown for feature/who-should-apply cards)
const ICON_KEYS = [
  // Emoji options
  '✅', '🔹', '📊', '💻', '🎓', '🚀', '🧠', '⚡',
  // React icon keys (resolved by iconMap.js on the public page)
  'FaLaptopCode', 'BsFillPersonCheckFill', 'LuSparkles', 'FaPersonChalkboard',
  'FaAward', 'FaGraduationCap', 'FaUserTie', 'FaExchangeAlt',
  'FaChartColumn', 'FaSquarePollVertical', 'FaMicrosoft', 'FaRegFileExcel',
  'FaPython', 'FaDatabase', 'FaServer', 'FaCodeBranch', 'FaCloud',
];

// ── Reusable sub-components ────────────────────────────────────────────────────

/** Custom Dropdown that renders React Icons next to key names */
const IconDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '220px', userSelect: 'none', zIndex: 99 }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '0.6rem',
          borderRadius: '6px',
          border: '1px solid rgba(0,0,0,0.12)',
          background: 'var(--color-white)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.2rem', display: 'flex', alignItems: 'center' }}>{resolveIcon(value)}</span>
          <span style={{ fontSize: '0.9rem', color: 'var(--color-dark)' }}>{value}</span>
        </div>
        <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '105%',
            left: 0,
            width: '100%',
            maxHeight: '200px',
            overflowY: 'auto',
            background: 'var(--color-white)',
            border: '1px solid rgba(0,0,0,0.12)',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 999,
          }}
        >
          {ICON_KEYS.map((k) => (
            <div
              key={k}
              onClick={() => {
                onChange(k);
                setIsOpen(false);
              }}
              style={{
                padding: '0.5rem 0.6rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem',
                background: value === k ? 'var(--color-light)' : 'transparent',
                color: 'var(--color-dark)',
              }}
              onMouseEnter={(e) => {
                if (value !== k) e.currentTarget.style.background = 'var(--color-light)';
              }}
              onMouseLeave={(e) => {
                if (value !== k) e.currentTarget.style.background = 'transparent';
              }}
            >
              <span style={{ fontSize: '1.2rem', display: 'flex', alignItems: 'center' }}>{resolveIcon(k)}</span>
              <span>{k}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Section wrapper with collapsible heading and optional copy control */
const Section = ({ title, children, copyControl }) => {
  const [open, setOpen] = useState(true);
  return (
    <div
      style={{
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '10px',
        overflow: 'hidden',
        marginBottom: '1rem',
        background: 'var(--color-white)',
      }}
    >
      <div
        style={{
          width: '100%',
          padding: '0.8rem 1rem',
          background: 'var(--color-light)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        <span
          onClick={() => setOpen((o) => !o)}
          style={{
            fontWeight: 700,
            fontSize: '1rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--color-dark)',
          }}
        >
          {title}
          <span style={{ opacity: 0.5, fontSize: '0.75rem', fontWeight: 'normal' }}>
            {open ? '▲ collapse' : '▼ expand'}
          </span>
        </span>
        {copyControl && <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>{copyControl}</div>}
      </div>
      {open && (
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          {children}
        </div>
      )}
    </div>
  );
};

/** Labeled text input */
const Field = ({ label, value, onChange, placeholder, required, type = 'text' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
    <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>{label}{required && ' *'}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      style={{
        padding: '0.6rem',
        borderRadius: '6px',
        border: '1px solid rgba(0,0,0,0.12)',
        background: 'var(--color-white)',
        fontFamily: 'inherit',
      }}
    />
  </div>
);

/** Labeled textarea */
const TextArea = ({ label, value, onChange, placeholder, rows = 3 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
    <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        padding: '0.6rem',
        borderRadius: '6px',
        border: '1px solid rgba(0,0,0,0.12)',
        background: 'var(--color-white)',
        fontFamily: 'inherit',
        resize: 'vertical',
      }}
    />
  </div>
);

/** Small delete-row button */
const RemoveBtn = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      background: 'rgba(211,47,47,0.1)',
      color: '#c62828',
      border: 'none',
      borderRadius: '6px',
      padding: '0.3rem 0.7rem',
      cursor: 'pointer',
      fontSize: '0.85rem',
      alignSelf: 'flex-start',
    }}
  >
    Remove
  </button>
);

/** Generic add-row button */
const AddBtn = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      background: 'var(--color-light)',
      border: '1px dashed rgba(0,0,0,0.2)',
      borderRadius: '6px',
      padding: '0.4rem 1rem',
      cursor: 'pointer',
      fontSize: '0.85rem',
      marginTop: '0.3rem',
    }}
  >
    + {label}
  </button>
);

// ── Helpers ────────────────────────────────────────────────────────────────────

const emptyStatistic = () => ({ value: '', label: '', ref: '' });
const emptyFeature = () => ({ iconKey: '✅', title: '', description: '' });
const emptyTool = () => ({ name: '', description: '', imageUrl: '' });
const emptyReason = () => ({ title: '', description: '' });
const emptyPhase = () => ({ title: '', description: '' });
const emptyWho = () => ({ iconKey: 'FaGraduationCap', title: '', description: '' });
const emptyVideo = () => ({ id: Date.now(), name: '', videoUrl: '' });

// ── Initial form state ─────────────────────────────────────────────────────────

const defaultForm = () => ({
  slug: '',
  title: '',
  shortTitle: '',
  cardDescription: '',
  cardImage: '',
  hero: {
    headline: '',
    subheadline: '',
    description: '',
    statistics: [emptyStatistic()],
  },
  programHighlights: {
    duration: '3 Months',
    features: [emptyFeature()],
  },
  toolsCovered: [emptyTool()],
  whyThisProgram: [emptyReason()],
  programDetails: {
    overview: { title: 'Program Overview and Duration', description: '', phases: [emptyPhase()] },
    modeOfDelivery: { title: 'Mode of Delivery', description: '' },
    commitment: { title: 'Commitment and Flexibility', description: '' },
    curriculum: { title: 'Curriculum and Hands-On Experience', description: '', points: [''] },
    mentorship: { title: 'Mentorship', description: '' },
    certification: { title: 'Certification and Credentials', description: '' },
    careerAdvancement: {
      title: 'Career Advancement',
      description: '',
      additionalInfo: '',
      careerPaths: '',
    },
  },
  whoShouldApply: [emptyWho()],
  aluminiVideos: [],
  contactEmail: 'hr@int-elligence.co.uk',
  footerNote: '',
  published: true,
  order: 0,
});

// ── Main Component ─────────────────────────────────────────────────────────────

const AdminCourseEdit = () => {
  const { themeState } = useThemeContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [form, setForm] = useState(defaultForm());
  const [coursesList, setCoursesList] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingIndexes, setUploadingIndexes] = useState({});
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 900;

  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

  // ── Fetch existing course for edit mode ──────────────────────────────────────
  useEffect(() => {
    fetchCoursesList();
    if (isEditMode) {
      fetchCourse();
    }
  }, [id]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const fetchCoursesList = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/courses/admin/all`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
        credentials: 'include',
      });
      const json = await res.json();
      if (res.ok && json.success && json.data) {
        setCoursesList(json.data);
      }
    } catch (err) {
      console.error('Error fetching list for copying:', err);
    }
  };

  const fetchCourse = async () => {
    try {
      setFetching(true);
      setError(null);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/courses/id/${id}`, {
        credentials: 'include',
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });

      if (res.status === 401 || res.status === 403) {
        navigate('/login');
        return;
      }

      const json = await res.json();
      if (res.ok && json.data) {
        // Merge fetched data over defaults so missing fields stay editable
        setForm((prev) => deepMerge(prev, json.data));
      } else {
        setError(json.message || 'Failed to load course');
      }
    } catch (err) {
      setError('Connection to backend failed.');
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  // ── Save handler ─────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!form.slug.trim() || !form.title.trim()) {
      setError('Slug and title are required.');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = isEditMode ? `${API_URL}/api/courses/${id}` : `${API_URL}/api/courses`;
      const method = isEditMode ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(form),
      });

      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      const json = await res.json();
      if (res.ok && json.success) {
        setSuccess(isEditMode ? 'Course updated successfully!' : 'Course created successfully!');
        setTimeout(() => navigate('/admin/courses'), 1500);
      } else {
        setError(json.message || 'Failed to save course.');
      }
    } catch (err) {
      setError('Failed to connect to the server.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Tool logo upload to MongoDB ──────────────────────────────────────────────
  const handleToolLogoUpload = async (idx, file) => {
    if (!file) return;
    try {
      setUploadingIndexes((prev) => ({ ...prev, [idx]: true }));
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/courses/upload-tool-logo`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: formData,
      });

      const json = await res.json();
      if (res.ok && json.success && json.url) {
        updateArray('toolsCovered', idx, 'imageUrl', json.url);
      } else {
        alert(json.message || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Logo upload error:', err);
      alert('Error connecting to the server for logo upload.');
    } finally {
      setUploadingIndexes((prev) => ({ ...prev, [idx]: false }));
    }
  };

  const handleBannerUpload = async (file) => {
    if (!file) return;
    try {
      setUploadingBanner(true);
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/courses/upload-tool-logo`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: formData,
      });

      const json = await res.json();
      if (res.ok && json.success && json.url) {
        setForm((prev) => ({ ...prev, cardImage: json.url }));
      } else {
        alert(json.message || 'Failed to upload banner');
      }
    } catch (err) {
      console.error('Banner upload error:', err);
      alert('Error connecting to the server for banner upload.');
    } finally {
      setUploadingBanner(false);
    }
  };

  // Array item updaters
  const updateArray = (key, idx, field, value) => {
    setForm((prev) => {
      const arr = [...(prev[key] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, [key]: arr };
    });
  };

  const addToArray = (key, emptyFn) => {
    setForm((prev) => ({ ...prev, [key]: [...(prev[key] || []), emptyFn()] }));
  };

  const removeFromArray = (key, idx) => {
    setForm((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== idx),
    }));
  };

  // Nested (hero, programDetails, etc.)
  const updateNested = (section, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const updateNestedArray = (section, key, idx, field, value) => {
    setForm((prev) => {
      const arr = [...(prev[section][key] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, [section]: { ...prev[section], [key]: arr } };
    });
  };

  // programDetails is two levels deep
  const updatePD = (sub, field, value) => {
    setForm((prev) => ({
      ...prev,
      programDetails: {
        ...prev.programDetails,
        [sub]: { ...prev.programDetails[sub], [field]: value },
      },
    }));
  };

  const updatePDArray = (sub, key, idx, value) => {
    setForm((prev) => {
      const arr = [...(prev.programDetails[sub][key] || [])];
      arr[idx] = value;
      return {
        ...prev,
        programDetails: {
          ...prev.programDetails,
          [sub]: { ...prev.programDetails[sub], [key]: arr },
        },
      };
    });
  };

  const updatePDArrayItem = (sub, key, idx, field, value) => {
    setForm((prev) => {
      const arr = [...(prev.programDetails[sub][key] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return {
        ...prev,
        programDetails: {
          ...prev.programDetails,
          [sub]: { ...prev.programDetails[sub], [key]: arr },
        },
      };
    });
  };

  // ── Render Helpers ──────────────────────────────────────────────────────────

  /**
   * Renders the "Copy from: [select]" control for each collapsible section.
   *
   * Root-cause fix: coursesList only contains lightweight fields (slug, title, shortTitle…).
   * It does NOT include section data like hero, programHighlights, toolsCovered, etc.
   * So we must fetch the FULL course by _id when the user picks a source course.
   */
  const renderCopyControl = (sectionKey, updateFn) => {
    if (coursesList.length === 0) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem' }}>
        <span style={{ opacity: 0.7, color: 'var(--color-dark)' }}>Copy from:</span>
        <select
          defaultValue=""
          onChange={async (e) => {
            const courseId = e.target.value;
            if (!courseId) return;

            // Reset immediately so the select doesn't stay on the chosen value
            e.target.value = '';

            try {
              // Fetch the FULL course data (not the lightweight list entry)
              const token = localStorage.getItem('token');
              const res = await fetch(`${API_URL}/api/courses/id/${courseId}`, {
                headers: { Authorization: token ? `Bearer ${token}` : '' },
              });
              const json = await res.json();

              if (res.ok && json.success && json.data) {
                const sectionData = json.data[sectionKey];
                if (sectionData !== undefined && sectionData !== null) {
                  updateFn(JSON.parse(JSON.stringify(sectionData)));
                } else {
                  // Section exists in schema but is empty for this course — warn in console only
                  console.warn(`Section "${sectionKey}" is empty or missing in the selected course.`);
                }
              } else {
                console.error('Failed to fetch course for copy:', json.message);
              }
            } catch (err) {
              console.error('Error fetching course for section copy:', err);
            }
          }}
          style={{
            padding: '0.2rem 0.4rem',
            borderRadius: '4px',
            border: '1px solid rgba(0,0,0,0.15)',
            background: 'var(--color-white)',
            fontSize: '0.8rem',
            maxWidth: '150px',
            color: 'var(--color-dark)',
          }}
        >
          <option value="" disabled>-- Select Course --</option>
          {coursesList.map((c) => (
            <option key={c._id} value={c._id}>
              {c.shortTitle || c.title}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const inputStyle = {
    padding: '0.6rem',
    borderRadius: '6px',
    border: '1px solid rgba(0,0,0,0.12)',
    background: 'var(--color-white)',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
    color: 'var(--color-dark)',
  };

  const formContent = (
    <>
      {/* ── 1. Basic Info ── */}
      <Section title="1. Basic Info">
        <Field
          label="Slug (URL identifier)"
          value={form.slug}
          onChange={(v) => setForm((p) => ({ ...p, slug: v.toLowerCase().replace(/\s+/g, '-') }))}
          placeholder="e.g. data-science"
          required
        />
        <p style={{ fontSize: '0.8rem', opacity: 0.6, margin: 0 }}>
          Must match the URL path: /virtual-internship/<strong>{form.slug || 'your-slug'}</strong>
        </p>
        <Field
          label="Full Title"
          value={form.title}
          onChange={(v) => setForm((p) => ({ ...p, title: v }))}
          placeholder="e.g. Data Science Training & Internship Program"
          required
        />
        <Field
          label="Short Title (for nav/listings)"
          value={form.shortTitle}
          onChange={(v) => setForm((p) => ({ ...p, shortTitle: v }))}
          placeholder="e.g. Data Science"
        />
        <TextArea
          label="Card Description (for homepage program grid)"
          value={form.cardDescription || ''}
          onChange={(v) => setForm((p) => ({ ...p, cardDescription: v }))}
          placeholder="e.g. Progress from core concepts to advanced Data Science techniques..."
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Card Banner Image (for homepage program grid)</label>
            {form.cardImage && form.cardImage.includes('/tool-image/') && (
              <span style={{ color: '#2e7d32', fontSize: '0.75rem', fontWeight: 'bold' }}>✅ Uploaded to DB</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {form.cardImage && (
              <img
                src={form.cardImage.startsWith('/') ? `${API_URL}${form.cardImage}` : form.cardImage}
                alt="Banner preview"
                style={{
                  width: '50px',
                  height: '35px',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  border: '1px solid rgba(0,0,0,0.1)',
                  background: '#fff',
                  padding: '2px',
                  flexShrink: 0
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <input
              type="text"
              value={form.cardImage || ''}
              onChange={(e) => setForm((p) => ({ ...p, cardImage: e.target.value }))}
              placeholder="e.g. /static/media/data_science.jpg or custom web URL"
              style={{ ...inputStyle, flexGrow: 1 }}
            />
            <input
              type="file"
              accept="image/*"
              id="course-banner-upload"
              style={{ display: 'none' }}
              onChange={(e) => handleBannerUpload(e.target.files[0])}
            />
            <label
              htmlFor="course-banner-upload"
              className="btn sm"
              style={{
                margin: 0,
                cursor: 'pointer',
                padding: '0.6rem 1rem',
                background: 'var(--color-primary)',
                color: 'white',
                borderRadius: '6px',
                fontSize: '0.85rem',
                textAlign: 'center',
                flexShrink: 0
              }}
            >
              {uploadingBanner ? 'Uploading...' : 'Upload Image'}
            </label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm((p) => ({ ...p, published: e.target.checked }))}
              style={{ width: '1.1rem', height: '1.1rem' }}
            />
            Published
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Display Order</label>
            <input
              type="number"
              value={form.order}
              onChange={(e) => setForm((p) => ({ ...p, order: Number(e.target.value) }))}
              style={{ ...inputStyle, width: '80px' }}
              min={0}
            />
          </div>
        </div>
      </Section>

      {/* ── 2. Hero ── */}
      <Section
        title="2. Hero Section"
        copyControl={renderCopyControl('hero', (val) => setForm((p) => ({ ...p, hero: val })))}
      >
        <Field label="Headline" value={form.hero.headline} onChange={(v) => updateNested('hero', 'headline', v)} placeholder="e.g. Data Science Training and Internship - Certificate Program" />
        <Field label="Subheadline" value={form.hero.subheadline} onChange={(v) => updateNested('hero', 'subheadline', v)} placeholder="UK Industry-Microsoft Partnered..." />
        <TextArea label="Description" value={form.hero.description} onChange={(v) => updateNested('hero', 'description', v)} placeholder="Join Int-Elligence Tech Solutions Plc and gain..." rows={3} />
        <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Statistics</label>
        {(form.hero.statistics || []).map((stat, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr 1fr auto', gap: '0.5rem', alignItems: 'end', background: 'var(--color-light)', padding: '0.5rem', borderRadius: '6px' }}>
            <input style={inputStyle} placeholder="Value (e.g. 82%)" value={stat.value} onChange={(e) => updateNestedArray('hero', 'statistics', i, 'value', e.target.value)} />
            <input style={inputStyle} placeholder="Label" value={stat.label} onChange={(e) => updateNestedArray('hero', 'statistics', i, 'label', e.target.value)} />
            <input style={inputStyle} placeholder="Ref/Source" value={stat.ref} onChange={(e) => updateNestedArray('hero', 'statistics', i, 'ref', e.target.value)} />
            <RemoveBtn onClick={() => {
              setForm((p) => ({ ...p, hero: { ...p.hero, statistics: p.hero.statistics.filter((_, j) => j !== i) } }));
            }} />
          </div>
        ))}
        <AddBtn label="Add Statistic" onClick={() => {
          setForm((p) => ({ ...p, hero: { ...p.hero, statistics: [...(p.hero.statistics || []), emptyStatistic()] } }));
        }} />
      </Section>

      {/* ── 3. Program Highlights ── */}
      <Section
        title="3. Program Highlights"
        copyControl={renderCopyControl('programHighlights', (val) => setForm((p) => ({ ...p, programHighlights: val })))}
      >
        <Field label="Duration" value={form.programHighlights.duration} onChange={(v) => updateNested('programHighlights', 'duration', v)} placeholder="e.g. 3 Months" />
        <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Feature Cards</label>
        {(form.programHighlights.features || []).map((feat, i) => (
          <div key={i} style={{ background: 'var(--color-light)', borderRadius: '8px', padding: '0.7rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--color-dark)' }}>Icon</label>
              <IconDropdown
                value={feat.iconKey}
                onChange={(icon) => updateNestedArray('programHighlights', 'features', i, 'iconKey', icon)}
              />
              <span style={{ fontSize: '1.2rem' }}>{resolveIcon(feat.iconKey)}</span>
              <RemoveBtn onClick={() => updateNested('programHighlights', 'features', form.programHighlights.features.filter((_, j) => j !== i))} />
            </div>
            <input style={inputStyle} placeholder="Title" value={feat.title} onChange={(e) => updateNestedArray('programHighlights', 'features', i, 'title', e.target.value)} />
            <textarea style={{ ...inputStyle, resize: 'vertical' }} placeholder="Description" value={feat.description} onChange={(e) => updateNestedArray('programHighlights', 'features', i, 'description', e.target.value)} rows={2} />
          </div>
        ))}
        <AddBtn label="Add Feature" onClick={() => updateNested('programHighlights', 'features', [...(form.programHighlights.features || []), emptyFeature()])} />
      </Section>

      {/* ── 4. Tools Covered ── */}
      <Section
        title="4. Tools & Technologies Covered"
        copyControl={renderCopyControl('toolsCovered', (val) => setForm((p) => ({ ...p, toolsCovered: val })))}
      >
        {(form.toolsCovered || []).map((tool, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr 2fr auto', gap: '0.5rem', alignItems: 'end', background: 'var(--color-light)', padding: '0.5rem', borderRadius: '6px' }}>
            <input style={inputStyle} placeholder="Name" value={tool.name} onChange={(e) => updateArray('toolsCovered', i, 'name', e.target.value)} />
            <input style={inputStyle} placeholder="Description" value={tool.description} onChange={(e) => updateArray('toolsCovered', i, 'description', e.target.value)} />
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.8, color: 'var(--color-dark)' }}>Logo Image</span>
                {tool.imageUrl && tool.imageUrl.includes('/tool-image/') && (
                  <span style={{ color: '#2e7d32', fontSize: '0.75rem', fontWeight: 'bold' }}>✅ Uploaded</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.3rem', width: '100%', alignItems: 'center' }}>
                {tool.imageUrl && (
                  <img
                    src={tool.imageUrl.startsWith('/') ? `${API_URL}${tool.imageUrl}` : tool.imageUrl}
                    alt="Logo preview"
                    style={{
                      width: '32px',
                      height: '32px',
                      objectFit: 'contain',
                      borderRadius: '4px',
                      border: '1px solid rgba(0,0,0,0.1)',
                      background: '#fff',
                      padding: '2px',
                      flexShrink: 0
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  id={`tool-logo-upload-${i}`}
                  style={{ display: 'none' }}
                  onChange={(e) => handleToolLogoUpload(i, e.target.files[0])}
                />
                <label
                  htmlFor={`tool-logo-upload-${i}`}
                  className="btn sm"
                  style={{
                    margin: 0,
                    cursor: 'pointer',
                    padding: '0.4rem 0.8rem',
                    background: 'var(--color-primary)',
                    color: 'white',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {uploadingIndexes[i] ? 'Uploading...' : 'Upload'}
                </label>
                <input
                  style={inputStyle}
                  placeholder="Or paste Logo URL"
                  value={tool.imageUrl || ''}
                  onChange={(e) => updateArray('toolsCovered', i, 'imageUrl', e.target.value)}
                />
              </div>
            </div>

            <RemoveBtn onClick={() => removeFromArray('toolsCovered', i)} />
          </div>
        ))}
        <AddBtn label="Add Tool" onClick={() => addToArray('toolsCovered', emptyTool)} />
      </Section>

      {/* ── 5. Why Choose Us ── */}
      <Section
        title="5. Why Choose Us"
        copyControl={renderCopyControl('whyThisProgram', (val) => setForm((p) => ({ ...p, whyThisProgram: val })))}
      >
        {(form.whyThisProgram || []).map((reason, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr auto', gap: '0.5rem', alignItems: 'end', background: 'var(--color-light)', padding: '0.5rem', borderRadius: '6px' }}>
            <input style={inputStyle} placeholder="Title" value={reason.title} onChange={(e) => updateArray('whyThisProgram', i, 'title', e.target.value)} />
            <textarea style={{ ...inputStyle, resize: 'vertical' }} placeholder="Description" value={reason.description} onChange={(e) => updateArray('whyThisProgram', i, 'description', e.target.value)} rows={2} />
            <RemoveBtn onClick={() => removeFromArray('whyThisProgram', i)} />
          </div>
        ))}
        <AddBtn label="Add Reason" onClick={() => addToArray('whyThisProgram', emptyReason)} />
      </Section>

      {/* ── 6. Program Details ── */}
      <Section
        title="6. Program Details"
        copyControl={renderCopyControl('programDetails', (val) => setForm((p) => ({ ...p, programDetails: val })))}
      >
        {/* Overview */}
        <label style={{ fontWeight: 700, color: 'var(--color-dark)' }}>Overview</label>
        <TextArea label="Overview Title" value={form.programDetails.overview.title} onChange={(v) => updatePD('overview', 'title', v)} />
        <TextArea label="Overview Description" value={form.programDetails.overview.description} onChange={(v) => updatePD('overview', 'description', v)} rows={3} />
        <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Phases</label>
        {(form.programDetails.overview.phases || []).map((phase, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr auto', gap: '0.5rem', alignItems: 'end', background: 'var(--color-light)', padding: '0.5rem', borderRadius: '6px' }}>
            <input style={inputStyle} placeholder="Phase title" value={phase.title} onChange={(e) => updatePDArrayItem('overview', 'phases', i, 'title', e.target.value)} />
            <textarea style={{ ...inputStyle, resize: 'vertical' }} placeholder="Phase description" value={phase.description} onChange={(e) => updatePDArrayItem('overview', 'phases', i, 'description', e.target.value)} rows={2} />
            <RemoveBtn onClick={() => updatePD('overview', 'phases', form.programDetails.overview.phases.filter((_, j) => j !== i))} />
          </div>
        ))}
        <AddBtn label="Add Phase" onClick={() => updatePD('overview', 'phases', [...(form.programDetails.overview.phases || []), emptyPhase()])} />

        {/* Remaining flat detail cards */}
        {[
          { key: 'modeOfDelivery', label: 'Mode of Delivery' },
          { key: 'commitment', label: 'Commitment' },
          { key: 'mentorship', label: 'Mentorship' },
          { key: 'certification', label: 'Certification' },
        ].map(({ key, label }) => (
          <div key={key} style={{ marginTop: '0.8rem' }}>
            <label style={{ fontWeight: 700, color: 'var(--color-dark)' }}>{label}</label>
            <TextArea label="Title" value={form.programDetails[key].title} onChange={(v) => updatePD(key, 'title', v)} />
            <TextArea label="Description" value={form.programDetails[key].description} onChange={(v) => updatePD(key, 'description', v)} rows={3} />
          </div>
        ))}

        {/* Curriculum with points */}
        <div style={{ marginTop: '0.8rem' }}>
          <label style={{ fontWeight: 700, color: 'var(--color-dark)' }}>Curriculum</label>
          <TextArea label="Title" value={form.programDetails.curriculum.title} onChange={(v) => updatePD('curriculum', 'title', v)} />
          <TextArea label="Description" value={form.programDetails.curriculum.description} onChange={(v) => updatePD('curriculum', 'description', v)} rows={3} />
          <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Points (bullet list)</label>
          {(form.programDetails.curriculum.points || []).map((pt, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.2rem' }}>
              <input style={inputStyle} placeholder="e.g. Learn key concepts..." value={pt} onChange={(e) => updatePDArray('curriculum', 'points', i, e.target.value)} />
              <RemoveBtn onClick={() => updatePD('curriculum', 'points', form.programDetails.curriculum.points.filter((_, j) => j !== i))} />
            </div>
          ))}
          <AddBtn label="Add Point" onClick={() => updatePD('curriculum', 'points', [...(form.programDetails.curriculum.points || []), ''])} />
        </div>

        {/* Career Advancement */}
        <div style={{ marginTop: '0.8rem' }}>
          <label style={{ fontWeight: 700, color: 'var(--color-dark)' }}>Career Advancement</label>
          <TextArea label="Title" value={form.programDetails.careerAdvancement.title} onChange={(v) => updatePD('careerAdvancement', 'title', v)} />
          <TextArea label="Description" value={form.programDetails.careerAdvancement.description} onChange={(v) => updatePD('careerAdvancement', 'description', v)} rows={3} />
          <TextArea label="Additional Info" value={form.programDetails.careerAdvancement.additionalInfo} onChange={(v) => updatePD('careerAdvancement', 'additionalInfo', v)} rows={2} />
          <TextArea label="Career Paths" value={form.programDetails.careerAdvancement.careerPaths} onChange={(v) => updatePD('careerAdvancement', 'careerPaths', v)} rows={2} />
        </div>
      </Section>

      {/* ── 7. Who Should Apply ── */}
      <Section
        title="7. Who Should Apply"
        copyControl={renderCopyControl('whoShouldApply', (val) => setForm((p) => ({ ...p, whoShouldApply: val })))}
      >
        {(form.whoShouldApply || []).map((who, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr 2fr auto', gap: '0.5rem', alignItems: 'center', background: 'var(--color-light)', padding: '0.5rem', borderRadius: '6px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.8, color: 'var(--color-dark)' }}>Icon</span>
              <IconDropdown
                value={who.iconKey}
                onChange={(icon) => updateArray('whoShouldApply', i, 'iconKey', icon)}
              />
            </div>
            <input style={inputStyle} placeholder="Title" value={who.title} onChange={(e) => updateArray('whoShouldApply', i, 'title', e.target.value)} />
            <textarea style={{ ...inputStyle, resize: 'vertical' }} placeholder="Description" value={who.description} onChange={(e) => updateArray('whoShouldApply', i, 'description', e.target.value)} rows={2} />
            <RemoveBtn onClick={() => removeFromArray('whoShouldApply', i)} />
          </div>
        ))}
        <AddBtn label="Add Card" onClick={() => addToArray('whoShouldApply', emptyWho)} />
      </Section>


      {/* ── 9. Footer & Misc ── */}
      <Section title="9. Footer & Miscellaneous">
        <Field label="Contact Email" value={form.contactEmail} onChange={(v) => setForm((p) => ({ ...p, contactEmail: v }))} placeholder="hr@int-elligence.co.uk" />
        <TextArea label="Footer Note" value={form.footerNote} onChange={(v) => setForm((p) => ({ ...p, footerNote: v }))} placeholder="The UK needs..." rows={2} />
      </Section>

      {/* ── Submit ── */}
      <button
        type="submit"
        className="btn primary"
        disabled={loading}
        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', width: 'fit-content', marginBottom: '3rem' }}
      >
        <FiSave /> {loading ? 'Saving...' : isEditMode ? 'Update Course' : 'Create Course'}
      </button>
    </>
  );

  return (
    <main className={`${themeState.primary} ${themeState.background} admin__page`}>
      <Navbar />

      <section className="admin__container" style={{ maxWidth: showPreview ? '100%' : '900px', transition: 'max-width 0.2s ease-in-out', padding: showPreview ? '0 2rem' : 'initial' }}>
        <br />
        {/* Page header */}
        <div
          className="admin__header"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button className="btn sm" onClick={() => navigate('/admin/courses')}>← Cancel</button>
            <h2 style={{ margin: 0 }}>{isEditMode ? 'Edit Course' : 'Create New Course'}</h2>
          </div>
          <div>
            <button
              type="button"
              className="btn"
              onClick={() => setShowPreview(!showPreview)}
              style={{
                background: showPreview ? 'var(--color-primary)' : 'var(--color-light)',
                color: showPreview ? 'white' : 'var(--color-dark)',
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
            >
              {showPreview ? 'Hide Live Preview' : 'Show Live Preview'}
            </button>
          </div>
        </div>

        {/* Status messages */}
        {error && <div className="admin__error">{error}</div>}
        {success && (
          <div className="admin__error" style={{ background: 'rgba(76,175,80,0.15)', color: '#2e7d32' }}>
            {success}
          </div>
        )}
        {fetching && <div className="admin__loading">Loading course data...</div>}

        {!fetching && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {showPreview ? (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.1fr', gap: '2rem', alignItems: 'start' }}>
                {/* Scrollable Left Pane: Form */}
                <div style={isMobile ? {} : { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', paddingRight: '0.5rem' }}>
                  {formContent}
                </div>
                {/* Scrollable Right Pane: Real-time Live Preview */}
                <div
                  style={{
                    border: '1px solid rgba(0,0,0,0.1)',
                    borderRadius: '10px',
                    boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.05)',
                    background: 'var(--color-bg)',
                    padding: '0.2rem',
                    ...(isMobile ? {} : { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' })
                  }}
                >
                  <CourseDetailPreview courseData={form} />
                </div>
              </div>
            ) : (
              formContent
            )}
          </form>
        )}
      </section>
    </main>
  );
};

// ── Utility helpers ─────────────────────────────────────────────────────────────

/**
 * Deep-merges `source` into `target` — used to fill in fetched course data
 * over the defaultForm() template so that any new/missing fields stay editable.
 */
function deepMerge(target, source) {
  if (!source || typeof source !== 'object') return target;
  const out = { ...target };
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      out[key] = deepMerge(out[key] || {}, source[key]);
    } else {
      out[key] = source[key];
    }
  }
  return out;
}

export default AdminCourseEdit;
